/**
 * Biblioteca de Validações Reutilizáveis
 *
 * Funções de validação para formulários que retornam mensagens de erro
 * em português claro ou undefined se válido.
 */

export type ValidationFunction = (value: string) => string | undefined;

/**
 * Valida se campo está preenchido
 * @param fieldName Nome do campo para mensagem de erro
 * @returns Função de validação
 * @example validateRequired('Email')('') // "Email é obrigatório"
 */
export const validateRequired = (fieldName: string): ValidationFunction => {
  return (value: string) => {
    if (!value || value.trim() === '') {
      return `${fieldName} é obrigatório`;
    }
    return undefined;
  };
};

/**
 * Valida formato de email
 * @param value Valor do campo email
 * @returns Mensagem de erro ou undefined
 * @example validateEmail('invalido') // "Email inválido. Exemplo: nome@empresa.com"
 */
export const validateEmail: ValidationFunction = (value: string) => {
  if (!value) return undefined; // Se vazio, validateRequired já cuidou

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return 'Email inválido. Exemplo: nome@empresa.com';
  }
  return undefined;
};

/**
 * Valida formato de telefone brasileiro
 * @param value Valor do campo telefone
 * @returns Mensagem de erro ou undefined
 * @example validatePhone('123') // "Telefone inválido. Use (11) 98765-4321"
 */
export const validatePhone: ValidationFunction = (value: string) => {
  if (!value) return undefined;

  // Remove tudo exceto números
  const numbersOnly = value.replace(/\D/g, '');

  if (numbersOnly.length < 10 || numbersOnly.length > 11) {
    return 'Telefone inválido. Use (11) 98765-4321';
  }
  return undefined;
};

/**
 * Valida número positivo (maior que zero)
 * @param fieldName Nome do campo para mensagem de erro
 * @returns Função de validação
 * @example validatePositiveNumber('Preço')('0') // "Preço deve ser um número maior que zero"
 */
export const validatePositiveNumber = (fieldName: string): ValidationFunction => {
  return (value: string) => {
    if (!value) return undefined;

    const num = parseFloat(value.replace(',', '.'));
    if (isNaN(num) || num <= 0) {
      return `${fieldName} deve ser um número maior que zero`;
    }
    return undefined;
  };
};

/**
 * Valida comprimento mínimo de string
 * @param min Número mínimo de caracteres
 * @param fieldName Nome do campo para mensagem de erro
 * @returns Função de validação
 * @example validateMinLength(6, 'Senha')('abc') // "Senha deve ter pelo menos 6 caracteres"
 */
export const validateMinLength = (min: number, fieldName: string): ValidationFunction => {
  return (value: string) => {
    if (!value) return undefined;

    if (value.length < min) {
      return `${fieldName} deve ter pelo menos ${min} caracteres`;
    }
    return undefined;
  };
};

/**
 * Valida comprimento máximo de string
 * @param max Número máximo de caracteres
 * @param fieldName Nome do campo para mensagem de erro
 * @returns Função de validação
 * @example validateMaxLength(50, 'Nome')('nome muito longo...') // "Nome deve ter no máximo 50 caracteres"
 */
export const validateMaxLength = (max: number, fieldName: string): ValidationFunction => {
  return (value: string) => {
    if (!value) return undefined;

    if (value.length > max) {
      return `${fieldName} deve ter no máximo ${max} caracteres`;
    }
    return undefined;
  };
};

/**
 * Valida se duas senhas coincidem
 * @param password Senha original para comparar
 * @returns Função de validação
 * @example validatePasswordMatch('senha123')('senha456') // "As senhas não coincidem"
 */
export const validatePasswordMatch = (password: string): ValidationFunction => {
  return (confirmPassword: string) => {
    if (confirmPassword && confirmPassword !== password) {
      return 'As senhas não coincidem';
    }
    return undefined;
  };
};

/**
 * Combina múltiplas validações (executa em ordem e retorna primeiro erro)
 * @param validators Lista de funções de validação
 * @returns Função de validação combinada
 * @example
 * combineValidations(
 *   validateRequired('Email'),
 *   validateEmail
 * )('') // "Email é obrigatório"
 */
export const combineValidations = (...validators: ValidationFunction[]): ValidationFunction => {
  return (value: string) => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) return error; // Retorna primeiro erro encontrado
    }
    return undefined;
  };
};

/**
 * Valida data no futuro (para encomendas)
 * @param value Valor do campo datetime-local
 * @returns Mensagem de erro ou undefined
 * @example validateFutureDate('2020-01-01T00:00') // "A data de entrega deve ser no futuro"
 */
export const validateFutureDate: ValidationFunction = (value: string) => {
  if (!value) return undefined;

  const selectedDate = new Date(value);
  const now = new Date();

  if (selectedDate <= now) {
    return 'A data de entrega deve ser no futuro';
  }
  return undefined;
};

/**
 * Valida número inteiro positivo (sem decimais)
 * @param fieldName Nome do campo para mensagem de erro
 * @returns Função de validação
 * @example validateInteger('Tempo de preparo')('10.5') // "Tempo de preparo deve ser um número inteiro"
 */
export const validateInteger = (fieldName: string): ValidationFunction => {
  return (value: string) => {
    if (!value) return undefined;

    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0 || value.includes('.') || value.includes(',')) {
      return `${fieldName} deve ser um número inteiro`;
    }
    return undefined;
  };
};

/**
 * Valida número não-negativo (>= 0)
 * @param fieldName Nome do campo para mensagem de erro
 * @returns Função de validação
 * @example validateNonNegativeNumber('Desconto')('-10') // "Desconto não pode ser negativo"
 */
export const validateNonNegativeNumber = (fieldName: string): ValidationFunction => {
  return (value: string) => {
    if (!value) return undefined;

    const num = parseFloat(value.replace(',', '.'));
    if (isNaN(num) || num < 0) {
      return `${fieldName} não pode ser negativo`;
    }
    return undefined;
  };
};
